/** Market Watch columns after the symbol, which is always shown. The user picks which, in this order. */
export const COLUMN_IDS = ['bid', 'ask', 'spread', 'change', 'high', 'low', 'time'] as const;
export type ColumnId = (typeof COLUMN_IDS)[number];

export const DEFAULT_COLUMNS: ColumnId[] = ['bid', 'ask', 'spread', 'change'];

export const COLUMN_INFO: Record<ColumnId, { header: string; label: string; title: string; width: string }> =
  {
    bid: { header: 'Bid', label: 'Bid', title: 'Bid price', width: '1fr' },
    ask: { header: 'Ask', label: 'Ask', title: 'Ask price', width: '1fr' },
    spread: { header: 'Spr.', label: 'Spread', title: 'Spread in points', width: '3.5rem' },
    change: { header: 'Day', label: 'Daily change', title: "Change since today's open", width: '4.5rem' },
    high: { header: 'High', label: 'Daily high', title: "Today's highest bid", width: '1fr' },
    low: { header: 'Low', label: 'Daily low', title: "Today's lowest bid", width: '1fr' },
    time: { header: 'Time', label: 'Time', title: 'Time of the last price (server time)', width: '4.5rem' },
  };

/** The stored column choice, repaired: known ids only, in the standard order; the default when nothing usable is stored. */
export function normalizeColumns(raw: unknown): ColumnId[] {
  if (!Array.isArray(raw)) return DEFAULT_COLUMNS;
  const chosen = new Set(raw.filter((c): c is ColumnId => (COLUMN_IDS as readonly unknown[]).includes(c)));
  return COLUMN_IDS.filter((c) => chosen.has(c));
}

export const toggleColumn = (columns: readonly ColumnId[], id: ColumnId): ColumnId[] =>
  columns.includes(id)
    ? columns.filter((c) => c !== id)
    : COLUMN_IDS.filter((c) => c === id || columns.includes(c));

/** The CSS grid template for the chosen columns: symbol first, the direction arrow and the remove button last. */
export const gridTemplate = (columns: readonly ColumnId[]) =>
  ['minmax(6rem,1.4fr)', ...columns.map((c) => COLUMN_INFO[c].width), '2.5rem'].join(' ');

export type SortKey = 'symbol' | ColumnId;

/**
 * The list sorted once by a column, as MT5 does it: the order itself changes (and is saved), so rows do not jump around
 * with every tick afterwards. Symbols without a value (no price yet) go last either way.
 */
export function sortWatchList(
  list: readonly string[],
  key: SortKey,
  dir: 'asc' | 'desc',
  valueOf: (name: string, key: SortKey) => number | string | null,
): string[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const x = valueOf(a, key);
    const y = valueOf(b, key);
    if (x === null || y === null) return x === y ? a.localeCompare(b) : x === null ? 1 : -1;
    if (x === y) return a.localeCompare(b);
    return (typeof x === 'string' ? x.localeCompare(y as string) : x - (y as number)) * sign;
  });
}

const DAYS: Record<string, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

/** A session spec ("24x7", or "Sun 21:00-Fri 22:00; …") as readable lines. */
export function describeSessions(spec: string): string[] {
  const trimmed = spec.trim();
  if (trimmed === '' || trimmed.toLowerCase() === '24x7') return ['Open all week, day and night'];
  return trimmed
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [from, to] = part.split('-').map((end) => end.trim());
      const nice = (end: string | undefined) => {
        const [day, time] = (end ?? '').split(/\s+/);
        return `${DAYS[(day ?? '').toLowerCase().slice(0, 3)] ?? day} ${time ?? ''}`.trim();
      };
      return `${nice(from)} to ${nice(to)}`;
    });
}
